import { Alert, Button, DialogContent, DialogTitle, Stack, Typography } from "@mui/material";
import { useCallback, useState } from "react";
import { withErrorHandling } from "@entrilia/shared/api/axiosHelper";
import DialogButtons from "@entrilia/shared/components/DialogButtons";
import DialogCloseButton from "@entrilia/shared/components/DialogCloseButton";
import InlineLoader from "@entrilia/shared/components/inlineLoader/InlineLoader";
import SmallDialog from "@entrilia/shared/components/SmallDialog";
import { useNotificationContext } from "@entrilia/shared/contexts/NotificationContext";
import useFetch from "@entrilia/shared/hooks/useFetch";
import { logError } from "@entrilia/shared/logging";
import adminApi from "../../../../api/adminApi";
import type { DemoRefreshOperation } from "../../../../api/types/demoRefreshTypes";
import useClientContext from "../../../../context/useClientContext";

interface Props {
  templateClientCode: string;
  onClose: () => void;
  onStarted: (operation: DemoRefreshOperation) => void;
}

const startDemoRefresh = withErrorHandling(adminApi.startDemoRefresh);

const DemoRefreshConfirmationDialog = ({ templateClientCode, onClose, onStarted }: Props) => {
  const { clientCode, title: companyTitle } = useClientContext();
  const { sendNotification, sendNotificationError } = useNotificationContext();
  const [loading, setLoading] = useState(false);
  const getDemoRefreshTemplate = useCallback(
    (signal?: AbortSignal) => adminApi.getDemoRefreshTemplate(clientCode, signal),
    [clientCode]
  );
  const [template, , { isFetching: isFetchingTemplate }] = useFetch(getDemoRefreshTemplate);
  const templateTitle = template?.title ?? templateClientCode;

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  const handleRefreshClick = async () => {
    setLoading(true);
    const [operation, error] = await startDemoRefresh(clientCode);
    setLoading(false);

    if (error) {
      logError(error, "[DemoRefreshConfirmationDialog] startDemoRefresh");
      sendNotificationError(error.message);
      return;
    }

    sendNotification("Demo company refresh started");
    onStarted(operation);
  };

  return (
    <SmallDialog open onClose={handleClose}>
      <DialogTitle sx={{ pr: 7 }}>Reset and refresh {companyTitle}?</DialogTitle>
      <DialogCloseButton onClick={handleClose} />
      <DialogContent>
        <Stack spacing={2}>
          {isFetchingTemplate ? (
            <InlineLoader />
          ) : (
            <Alert severity="warning">
              The accounting data, documents and Investor Portal message history of <strong>{companyTitle}</strong> will
              be replaced with a fresh copy of <strong>{templateTitle}</strong>, and its per-entity access settings are
              reset. Changes made in those areas will be lost.
            </Alert>
          )}
          <Typography>User accounts and their sign-in access are kept.</Typography>
          <Typography>
            A refresh takes from about 15 minutes to over an hour, depending on the company&apos;s size. You can keep
            working elsewhere; the Refresh Demo Company button stays disabled until it finishes.
          </Typography>
        </Stack>
      </DialogContent>
      <DialogButtons onCancel={handleClose} cancelDisabled={loading}>
        <Button variant="contained" loading={loading} disabled={isFetchingTemplate} onClick={handleRefreshClick}>
          Reset and refresh
        </Button>
      </DialogButtons>
    </SmallDialog>
  );
};

export default DemoRefreshConfirmationDialog;
